import React, { useEffect, useMemo, useState } from 'react'

export interface ThemeColors {
    primaryColor: string
    secondaryColor: string
    darkSecondaryColor: string
}

const svgTextCache = new Map<string, string>()
const svgFetchPromises = new Map<string, Promise<string>>()

const fetchSvgText = (src: string): Promise<string> => {
    if (svgTextCache.has(src)) return Promise.resolve(svgTextCache.get(src) as string)
    if (svgFetchPromises.has(src)) return svgFetchPromises.get(src) as Promise<string>
    const promise = fetch(src).then((res) => res.text()).then((text) => {
        svgTextCache.set(src, text)
        return text
    })
    svgFetchPromises.set(src, promise)
    return promise
}

const shade = (hex: string, percent: number) => {
    const clean = hex.replace('#', '')
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean
    const num = parseInt(full, 16)
    const clamp = (v: number) => Math.min(255, Math.max(0, v))
    const r = clamp((num >> 16) + Math.round(2.55 * percent))
    const g = clamp(((num >> 8) & 0xFF) + Math.round(2.55 * percent))
    const b = clamp((num & 0xFF) + Math.round(2.55 * percent))
    return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`
}

// The variant SVGs are Figma screenshot exports that use this fixed set of
// brand colours throughout their vector chrome. We swap these exact hex
// values for the admin's chosen colours at render time only — the source
// file on disk is never touched, so other institutions still see the
// original asset.
const recolorMap = ({ primaryColor, secondaryColor, darkSecondaryColor }: ThemeColors): [string, string][] => [
    ['#0D968F', primaryColor],
    ['#0EA59D', shade(primaryColor, 10)],
    ['#064542', darkSecondaryColor],
    ['#E7F6F5', secondaryColor],
    ['#B4E3E1', shade(secondaryColor, -20)],
    ['#D9EBEA', shade(secondaryColor, -10)],
]

const applyTheme = (svgText: string, colors: ThemeColors, imageOverrideUrl?: string) => {
    let out = svgText
    recolorMap(colors).forEach(([hex, value]) => {
        if (out.includes(hex)) out = out.split(hex).join(value)
    })
    if (imageOverrideUrl) {
        out = out.replace(/xlink:href="data:image\/[a-zA-Z+]+;base64,[^"]*"/, `xlink:href="${imageOverrideUrl}"`)
    }
    return out
}

export interface ThemedVariantSvgProps extends ThemeColors {
    src: string
    imageOverrideUrl?: string
    className?: string
}

// Renders a variant SVG inline (instead of <img>) so its fill colours can be
// swapped per-render. Colour changes are debounced since the native colour
// input fires continuously while dragging, and these source files can be
// several MB.
export const ThemedVariantSvg: React.FC<ThemedVariantSvgProps> = ({
                                                                       src, primaryColor, secondaryColor, darkSecondaryColor, imageOverrideUrl, className = '',
                                                                   }) => {
    const [rawSvg, setRawSvg] = useState<string | null>(null)
    const [colors, setColors] = useState<ThemeColors>({ primaryColor, secondaryColor, darkSecondaryColor })

    useEffect(() => {
        let cancelled = false
        setRawSvg(null)
        fetchSvgText(src).then((text) => { if (!cancelled) setRawSvg(text) })
        return () => { cancelled = true }
    }, [src])

    useEffect(() => {
        const timer = setTimeout(() => setColors({ primaryColor, secondaryColor, darkSecondaryColor }), 120)
        return () => clearTimeout(timer)
    }, [primaryColor, secondaryColor, darkSecondaryColor])

    const markup = useMemo(
        () => (rawSvg ? applyTheme(rawSvg, colors, imageOverrideUrl) : null),
        [rawSvg, colors, imageOverrideUrl],
    )

    if (!markup) {
        return <img src={src} alt="" className={className} />
    }

    return <div className={className} dangerouslySetInnerHTML={{ __html: markup }} />
}

export default ThemedVariantSvg
